import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Markdown from "@/components/Markdown";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, FileText, Building2, Search, Bot, Network, ChevronRight, Loader2 } from "lucide-react";

const ICONS = { company: Building2, seo: Search, scraper: Network, agent: Bot };

export default function Reports() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    api.get("/services/history")
      .then((r) => setItems(r.data.history || []))
      .catch((e) => { setItems([]); toast.error(formatApiError(e)); });
  }, []);

  const openReport = async (id) => {
    setLoadingDetail(true); setSelected(null);
    try {
      const { data } = await api.get(`/services/result/${id}`);
      setSelected(data.result);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingDetail(false); }
  };

  const inputSummary = (doc) => {
    const inp = doc.input || {};
    return inp.name || inp.url || inp.goal || "—";
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F2F2F2]" data-testid="reports-page">
      <Header />
      <main className="flex-1 max-w-6xl w-full mx-auto px-5 md:px-8 py-12 md:py-16">
        {selected ? (
          <>
            <button
              onClick={() => setSelected(null)}
              className="flex items-center gap-2 font-mono-aq text-[0.7rem] uppercase tracking-[0.2em] text-[#4A4A4A] hover:text-[#0A0A0A] mb-8"
              data-testid="report-back-list"
            >
              <ArrowLeft size={14} /> {t.dashboard.reports_back}
            </button>
            <div className="flex items-start gap-5 mb-8">
              <div className="w-14 h-14 rounded-2xl border border-[#B6B09F] flex items-center justify-center text-[#1E3A5F]">
                {(() => { const I = ICONS[selected.service] || FileText; return <I size={22} />; })()}
              </div>
              <div>
                <div className="label-eyebrow mb-2">— {t.dashboard.service_titles[selected.service] || selected.service}</div>
                <h1 className="font-serif-display text-3xl md:text-4xl tracking-tighter">{inputSummary(selected)}</h1>
                <div className="font-mono-aq text-[0.65rem] tracking-[0.22em] uppercase text-[#B6B09F] mt-3">
                  {new Date(selected.created_at).toLocaleString(lang === "tr" ? "tr-TR" : "en-US")}
                </div>
              </div>
            </div>

            <section className="aq-card p-6 md:p-10" data-testid="report-detail">
              {selected.report && <Markdown>{selected.report}</Markdown>}
              {selected.metrics && (
                <details className="mt-6">
                  <summary className="cursor-pointer text-sm font-mono-aq tracking-wider uppercase text-[#4A4A4A]">Ham metrikler</summary>
                  <pre className="mt-3 text-xs bg-[#EAE4D5] rounded p-4 overflow-x-auto">{JSON.stringify(selected.metrics, null, 2)}</pre>
                </details>
              )}
              {selected.result && !selected.report && (
                <pre className="text-xs bg-[#EAE4D5] rounded p-4 overflow-x-auto">{JSON.stringify(selected.result, null, 2)}</pre>
              )}
            </section>
          </>
        ) : (
          <>
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 font-mono-aq text-[0.7rem] uppercase tracking-[0.2em] text-[#4A4A4A] hover:text-[#0A0A0A] mb-8"
              data-testid="reports-back-dash"
            >
              <ArrowLeft size={14} /> {t.dashboard.back}
            </button>

            <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
              <div>
                <div className="label-eyebrow mb-3">— {t.dashboard.reports}</div>
                <h1 className="font-serif-display text-4xl md:text-6xl tracking-tighter leading-tight">
                  {t.dashboard.reports}
                </h1>
              </div>
              <div className="font-mono-aq text-[0.65rem] tracking-[0.22em] uppercase text-[#B6B09F]">
                {items ? `${items.length} kayıt` : "…"}
              </div>
            </div>

            {items === null ? (
              <div className="aq-card p-10 flex items-center gap-3 text-[#4A4A4A]"><Loader2 size={16} className="animate-spin" /> Yükleniyor…</div>
            ) : items.length === 0 ? (
              <div className="aq-card p-10 text-center" data-testid="reports-empty">
                <FileText size={22} className="mx-auto text-[#B6B09F] mb-4" />
                <p className="text-[#4A4A4A] max-w-md mx-auto">{t.dashboard.reports_empty}</p>
                <button onClick={() => navigate("/dashboard")} className="aq-button-primary mt-6" data-testid="reports-empty-cta">
                  {t.dashboard.back}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((it) => {
                  const I = ICONS[it.service] || FileText;
                  return (
                    <button
                      key={it.id}
                      onClick={() => openReport(it.id)}
                      disabled={loadingDetail}
                      className="w-full aq-card p-5 md:p-6 flex items-center gap-5 text-left hover:border-[#0A0A0A] transition-colors"
                      data-testid={`report-item-${it.id}`}
                    >
                      <div className="w-11 h-11 rounded-xl border border-[#B6B09F] flex items-center justify-center text-[#1E3A5F] shrink-0">
                        <I size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-serif-display text-xl truncate">{inputSummary(it)}</div>
                        <div className="font-mono-aq text-[0.65rem] tracking-[0.22em] uppercase text-[#B6B09F] mt-1">
                          {t.dashboard.service_titles[it.service] || it.service} ·{" "}
                          {new Date(it.created_at).toLocaleString(lang === "tr" ? "tr-TR" : "en-US")}
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-[#4A4A4A] shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
